import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { CheckCircle2, XCircle, AlertTriangle, Info, Loader2, X } from 'lucide-react'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { useToastStore, type Toast, type ToastType } from '../store/toastStore'

const STYLES: Record<ToastType, { icon: any; accent: string; bar: string; iconColor: string }> = {
  success: { icon: CheckCircle2, accent: 'border-l-emerald-500', bar: 'bg-emerald-500', iconColor: 'text-emerald-500' },
  error:   { icon: XCircle,      accent: 'border-l-red-500',     bar: 'bg-red-500',     iconColor: 'text-red-500' },
  warning: { icon: AlertTriangle,accent: 'border-l-amber-500',   bar: 'bg-amber-500',   iconColor: 'text-amber-500' },
  info:    { icon: Info,         accent: 'border-l-primary-500', bar: 'bg-primary-500', iconColor: 'text-primary-500' },
  loading: { icon: Loader2,      accent: 'border-l-primary-500', bar: 'bg-primary-500', iconColor: 'text-primary-500' },
}

function ToastItem({ toast }: { toast: Toast }) {
  const { t } = useTranslation()
  const dismiss = useToastStore((s) => s.dismiss)
  const [paused, setPaused] = useState(false)
  const [remaining, setRemaining] = useState(toast.duration)
  const s = STYLES[toast.type]
  const Icon = s.icon

  // Odpočet do zmizení (pauza při hoveru). Nezávisí na `remaining` v deps schválně —
  // restart jen při změně pauzy/typu/délky, jinak by se interval resetoval každý tik.
  useEffect(() => {
    if (toast.duration <= 0 || paused) return
    const start = Date.now()
    const from = remaining
    const id = window.setInterval(() => {
      const rem = from - (Date.now() - start)
      setRemaining(rem)
      if (rem <= 0) { window.clearInterval(id); dismiss(toast.id) }
    }, 50)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, toast.duration, toast.id])

  const pct = toast.duration > 0 ? Math.max(0, Math.min(100, (remaining / toast.duration) * 100)) : 0

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 40, scale: 0.96 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 40, scale: 0.96, transition: { duration: 0.18 } }}
      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      role="status"
      aria-live="polite"
      className={clsx(
        'relative overflow-hidden pointer-events-auto w-full rounded-xl border border-l-4 shadow-lg',
        'bg-white/95 dark:bg-zinc-900/95 backdrop-blur border-gray-100 dark:border-white/10',
        s.accent
      )}
    >
      <div className="flex items-start gap-3 p-3.5 pr-9">
        <Icon className={clsx('w-5 h-5 shrink-0 mt-0.5', s.iconColor, toast.type === 'loading' && 'animate-spin')} />
        <div className="min-w-0 flex-1">
          {toast.title && <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">{toast.title}</p>}
          <p className={clsx('text-sm text-gray-600 dark:text-gray-300 leading-snug break-words', toast.title && 'mt-0.5')}>{toast.message}</p>
        </div>
      </div>
      <button
        onClick={() => dismiss(toast.id)}
        className="absolute top-2.5 right-2.5 text-gray-300 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-200 transition"
        aria-label={t('common.close')}
      >
        <X className="w-4 h-4" />
      </button>
      {toast.duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-100 dark:bg-white/5">
          <div className={clsx('h-full', s.bar)} style={{ width: `${pct}%`, transition: 'width 50ms linear' }} />
        </div>
      )}
    </motion.div>
  )
}

export default function Toaster() {
  const toasts = useToastStore((s) => s.toasts)

  // „Flash" toast, který přežil reload (např. po sociálním loginu s window.location).
  useEffect(() => {
    const raw = sessionStorage.getItem('flash_toast')
    if (!raw) return
    sessionStorage.removeItem('flash_toast')
    try {
      const f = JSON.parse(raw)
      if (f?.message) useToastStore.getState().add({ type: f.type || 'success', message: f.message, duration: 4500 })
    } catch { /* ignore */ }
  }, [])

  return (
    <div className="fixed bottom-4 right-4 z-[300] flex flex-col gap-2 w-[360px] max-w-[calc(100vw-2rem)] pointer-events-none">
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} />
        ))}
      </AnimatePresence>
    </div>
  )
}
